/**
 * @fileoverview Instructor Programs (/instructors/:userId/programs), formerly
 * "Marketplace".
 *
 * Three tabs over one idea — the programs in this instructor's working life:
 *
 *   My programs   what I teach (or used to teach) in, from /marketplace/memberships
 *   Opportunities listings I can apply to, from /marketplace/programs
 *   Invites       invitations addressed to me, from /marketplace/invites
 *
 * The last two are switchable from the ops console (PROGRAM_OPPORTUNITIES_ENABLED
 * / PROGRAM_INVITES_ENABLED, both default ON) so Hans staff can take a surface
 * down platform-wide without a rebuild. My programs is not switchable and must
 * not become so: it is the instructor's own working life, and half of it — the
 * Organizations block — is the Spec 11 consent-withdrawal path.
 *
 * "Marketplace" described a shop, which this is not, and the page only ever
 * showed listings to apply to — the programs an instructor already taught in
 * lived nowhere but the attribution dropdown on the slot form.
 *
 * No org feature gate. The `marketplace` toggle is retired: an org governs its
 * own listings through each listing's `visibility`, never what its instructors
 * may see of the wider Hans community, and half this page is now the
 * instructor's own memberships, which no org may hide from them.
 */

import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import api, { getErrorMessage } from "@/lib/api";
import { programsPath, programDetailPath, programClassPath } from "@/lib/programsRoute";
import { useTabParam } from "@/lib/tabParam";
import ProgramStudentsTab from "@/components/ProgramStudentsTab";
import ProgramLessonsTab from "@/components/ProgramLessonsTab";
import UpdatesInbox from "@/components/UpdatesInbox";
import ResourceTiles from "@/components/ResourceTiles";
import RichText from "@/components/RichText";
import { programOpportunitiesEnabled, programInvitesEnabled, SCHEDULING_ENABLED, SCHEDULER_VIEW_ENABLED } from "@/lib/featureFlags";
import { Card, Eyebrow, Tag, Button, SegmentedControl, TextTabs, Icon, ListRow, PageShell } from "@/components/studio/primitives";
import OrgInviteBanner from "@/components/OrgInviteBanner";
import OrgMembershipCard from "@/components/OrgMembershipCard";
import ProgramsMap from "@/components/studio/ProgramsMap";
import {
  withDistance,
  withinRadius,
  distanceLabel,
  hasCoords,
  matchingInstruments,
  RADIUS_OPTIONS,
  RADIUS_DEFAULT,
} from "@/lib/geo";

const APP_STATUS = {
  pending: { label: "Applied", accent: true },
  accepted: { label: "Accepted", accent: true },
  declined: { label: "Declined", accent: false },
  withdrawn: { label: "Withdrawn", accent: false },
};

/** " · "-joined detail line, skipping anything unset. */
function metaLine(parts) {
  return parts.filter(Boolean).join(" · ");
}

/** An ISO instant as a bare "Sep 15" — a next-session hint, not a timestamp. */
function dateOnly(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function dateRange(start, end) {
  if (start && end) return `${start} – ${end}`;
  if (start) return `Starts ${start}`;
  if (end) return `Ends ${end}`;
  return null;
}

/**
 * A program's instruments, with the ones this instructor already teaches
 * picked out.
 *
 * The highlight is the accent plus a weight change rather than a badge per
 * instrument: a listing wanting four instruments would otherwise become four
 * competing chips, and the point is to let someone's eye land on the row that
 * concerns them while scanning, not to decorate every line.
 */
function InstrumentList({ instruments, teaches }) {
  const list = instruments || [];
  if (list.length === 0) return null;
  const matches = new Set(matchingInstruments(list, teaches));
  return (
    <span>
      {list.map((inst, i) => (
        <span key={inst}>
          <span
            className={
              matches.has(inst)
                ? "font-semibold text-[var(--accent-yellow-text)]"
                : "text-[var(--text-primary)]"
            }
            data-testid={matches.has(inst) ? `instrument-match-${inst}` : undefined}
          >
            {inst}
          </span>
          {i < list.length - 1 && <span className="text-[var(--text-tertiary)]">, </span>}
        </span>
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ tiles */

/** The corner marker for news: how many changes the org has made here that
    this instructor has not read yet. Deliberately a number rather than a dot —
    "three things changed" and "something changed" ask for different amounts of
    attention, and the page can afford to say which. */
function UnreadBadge({ count }) {
  if (!count) return null;
  return (
    <span
      data-testid="program-unread"
      aria-label={`${count} unread update${count === 1 ? "" : "s"}`}
      className="shrink-0 rounded-full bg-[var(--accent-yellow)] px-2 py-px font-mono text-[11px] font-semibold text-[var(--text-inverse)]"
    >
      {count}
    </span>
  );
}

function MembershipTile({ program, onOpen }) {
  const { active } = program;
  return (
    <Card
      onPress={onOpen}
      hover
      ariaLabel={`Open ${program.title}`}
      data-testid={`program-tile-${program.program_id}`}
      className="w-full cursor-pointer text-left"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <span className="block truncate font-sans text-[17px] font-semibold text-[var(--text-primary)]">{program.title}</span>
          <p className="mt-0.5 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">{program.org_name}</p>
          <p className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]">
            {metaLine([
              (program.instruments || []).join(", "),
              program.location,
              program.meeting_times,
              dateRange(program.start_date, program.end_date),
            ])}
          </p>
        </div>
        {/* The trailing edge (I4): the state pill, and beside it the one
            thing that changes — "the org did something you haven't seen".
            The tag describes YOUR affiliation, so membership decides it
            first: "Ended" is only for a program you're STILL in that has
            closed; reading `status` first would relabel every program you
            left as "Ended" the moment the org archived it. */}
        <div className="flex shrink-0 items-center gap-2">
          <UnreadBadge count={program.unseen_updates} />
          {active ? <Tag accent>Active</Tag> : <Tag>{program.is_member ? "Ended" : "Inactive"}</Tag>}
        </div>
      </div>
    </Card>
  );
}

/**
 * One invitation, with the two things the instructor needs to decide: who is
 * asking and what the work is.
 *
 * Accept and Decline sit side by side with equal weight. Making Decline quiet
 * would push people toward accepting work they don't want, which costs the org
 * more than a decline does.
 */
function InviteCard({ invite, onAccept, onDecline, busy, teaches }) {
  const p = invite.program;
  const decided = invite.status !== "pending";
  const [overviewOpen, setOverviewOpen] = useState(false);
  const hasOverview = p.instruments?.length > 0 || p.meeting_times;
  return (
    <Card data-testid={`invite-${invite.invite_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-sans text-[17px] font-semibold text-[var(--text-primary)]">{p.title}</span>
        {decided ? (
          <Tag>{invite.status === "accepted" ? "Accepted" : invite.status === "declined" ? "Declined" : "Withdrawn"}</Tag>
        ) : (
          <Tag accent>Invitation</Tag>
        )}
      </div>
      <p className="mt-0.5 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
        {p.org_name} invited you to teach
      </p>

      {invite.message && (
        <p className="mt-3 border-l-2 border-[var(--studio-divider-strong)] pl-3 font-sans text-[14px] italic leading-relaxed text-[var(--text-secondary)]">
          “{invite.message}”
        </p>
      )}

      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {[
          ["Pay", p.compensation],
          ["Location", metaLine([p.location, distanceLabel(p.distance_miles)])],
          ["Program dates", dateRange(p.start_date, p.end_date)],
        ]
          .filter(([, v]) => v)
          .map(([label, value]) => (
            <div key={label}>
              <dt className="font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">{label}</dt>
              <dd className="mt-0.5 font-sans text-[14px] text-[var(--text-primary)]">{value}</dd>
            </div>
          ))}
      </dl>

      {hasOverview && (
        <div className="mt-3">
          <Button
            variant="ghost"
            onClick={() => setOverviewOpen(!overviewOpen)}
            aria-expanded={overviewOpen}
            aria-controls={`invite-overview-${invite.invite_id}`}
          >
            Program overview {overviewOpen ? "−" : "+"}
          </Button>
          {overviewOpen && (
            <div id={`invite-overview-${invite.invite_id}`} className="mt-2">
              <p className="font-sans text-[13px] text-[var(--text-secondary)]">
                These details describe the whole program. Your teaching arrangements are agreed with {p.org_name}.
              </p>
              <dl className="mt-3 space-y-2">
                {p.instruments?.length > 0 && (
                  <div>
                    <dt className="font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">Program instruments</dt>
                    <dd className="mt-0.5 font-sans text-[14px] text-[var(--text-primary)]">
                      <InstrumentList instruments={p.instruments} teaches={teaches} />
                    </dd>
                  </div>
                )}
                {p.meeting_times && (
                  <div>
                    <dt className="font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">Program availability</dt>
                    <dd className="mt-0.5 font-sans text-[14px] text-[var(--text-primary)]">{p.meeting_times}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      )}

      {!decided && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={onAccept} disabled={busy} data-testid={`invite-accept-${invite.invite_id}`}>
            Accept
          </Button>
          <Button variant="ghost" onClick={onDecline} disabled={busy} data-testid={`invite-decline-${invite.invite_id}`}>
            Decline
          </Button>
        </div>
      )}
      {!decided && (
        <p className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]">
          Accepting adds you to the program straight away. Declining tells {p.org_name} and nobody else.
        </p>
      )}
    </Card>
  );
}

/* ----------------------------------------------------------- detail view */

/* A member's program is asked two questions — how it is going, and who is in
   it — so it has two tabs (I7). The tab and the class filter live in the URL,
   so a link, a refresh and Back land on the same one. Leave stays on the
   Overview (decision D8). */
const PROGRAM_TABS = [
  { id: "overview", label: "Overview", testId: "program-tab-overview" },
  { id: "updates", label: "Updates", testId: "program-tab-updates" },
  { id: "students", label: "Students", testId: "program-tab-students" },
  { id: "lessons", label: "Lessons", testId: "program-tab-lessons" },
];

function ProgramDetail({ programId, onBack, onLeft, teaches, onApplied }) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const { tab, filter, setTab } = useTabParam(PROGRAM_TABS, "overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaving, setLeaving] = useState(false);
  const [message, setMessage] = useState("");
  const [applying, setApplying] = useState(false);
  // The digit on the Updates tab: seeded by the program read, then kept by
  // the tab itself as rows are opened.
  const [unseen, setUnseen] = useState(0);
  // A class the org archived (O5) is kept off the list until asked for.
  const [showArchived, setShowArchived] = useState(false);

  // One URL per program, whichever side of it you're on. The membership
  // endpoint 404s for someone who never taught here, which is exactly the
  // person following a Details link off an opportunity — so fall back to the
  // listing rather than making opportunities a second route.
  useEffect(() => {
    let cancelled = false;
    api
      .get(`/marketplace/memberships/${programId}`)
      .catch((err) => {
        if (err?.response?.status !== 404) throw err;
        return api.get(`/marketplace/programs/${programId}`).then((res) => ({
          data: { program: res.data.program, students: [], listing_only: true },
        }));
      })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setUnseen(res.data?.unseen_updates || 0);
      })
      .catch((err) => {
        if (!cancelled) toast.error(getErrorMessage(err, "Couldn't load that program"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [programId]);

  const apply = async () => {
    setApplying(true);
    try {
      await api.post(`/marketplace/programs/${programId}/apply`, { message: message.trim() || undefined });
      toast.success("Application sent");
      onApplied?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't send your application"));
    } finally {
      setApplying(false);
    }
  };

  const leave = async () => {
    if (
      !window.confirm(
        "Leave this program?\n\nEvery lesson you've taught under it is kept, exactly as it is. You just won't be able to attribute new lessons to it.",
      )
    )
      return;
    setLeaving(true);
    try {
      await api.delete(`/marketplace/programs/${programId}/membership`);
      toast.success("You've left the program");
      onLeft();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't leave the program"));
    } finally {
      setLeaving(false);
    }
  };

  if (loading) return <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>;
  if (!data) return null;

  const {
    program,
    classes = [],
    classes_complete: classesComplete = true,
  } = data;
  const liveClasses = classes.filter((c) => !c.archived);
  const archivedClasses = classes.filter((c) => c.archived);
  const shownClasses = showArchived ? classes : liveClasses;
  // The school's people and the organization's own. An older server sends
  // only the single contact, which reads as a school contact list of one.
  const legacyContact = program.contact_email || program.contact_phone || program.contact_name
    ? [{ name: program.contact_name, email: program.contact_email, phone: program.contact_phone }]
    : [];
  const schoolContacts = program.school_contacts || legacyContact;
  const orgContacts = program.org_contacts || [];
  const resources = program.resources || [];
  const notesOwed = classes.reduce((n, c) => n + (c.notes_missing || 0), 0);

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-1.5 font-sans text-[14px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      >
        <Icon name="chevronLeft" size={16} />
        All programs
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">{program.title}</h1>
        {program.active ? <Tag accent>Active</Tag> : <Tag>{program.is_member ? "Ended" : "Inactive"}</Tag>}
      </div>
      <p className="mt-1 font-sans text-[14px] text-[var(--text-tertiary)]">{program.org_name}</p>
      {program.description && (
        <p className="mt-2 max-w-3xl font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">{program.description}</p>
      )}
      <div className="mb-6" />

      {/* Four tabs for a member (I5); a listing you don't teach in has only
          the one. Updates carries its unread count, the one number on this
          page that is news. */}
      {!data.listing_only && (
        <div className="-mx-4 mb-6 overflow-x-auto px-4 md:mx-0 md:px-0">
          <SegmentedControl
            testId="program-tabs"
            className="whitespace-nowrap"
            value={tab}
            onChange={(id) => setTab(id)}
            options={PROGRAM_TABS.map((t) => (t.id === "updates" && unseen > 0 ? { ...t, count: unseen } : t))}
          />
        </div>
      )}

      {/* ---- Updates ------------------------------------------------- */}
      {/* The Inbox narrowed to this program (I6, decision D9): each row opens
          the class it describes and is read by that click alone; Mark all as
          read is the broom. The tab's digit follows the list. */}
      {tab === "updates" && !data.listing_only && (
        <UpdatesInbox programId={programId} heading="What changed" onUnread={setUnseen} />
      )}

      {tab === "students" && !data.listing_only && (
        <ProgramStudentsTab
          programId={programId}
          programTitle={program.title}
          classFilter={filter}
          onClassFilter={(id) => setTab("students", id || undefined)}
        />
      )}

      {/* What you taught here and what you still owe (I9), the same filter
          bar the organization uses; the status lives in the URL. */}
      {tab === "lessons" && !data.listing_only && (
        <ProgramLessonsTab
          programId={programId}
          classes={classes}
          filter={filter}
          onFilter={(status) => setTab("lessons", status || undefined)}
        />
      )}

      {(tab === "students" || tab === "lessons" || tab === "updates") && !data.listing_only ? null : (
      <>

      {/* The classes this instructor teaches HERE, ahead of the listing detail:
          for a member this is the program — the description and pay line are
          what they read once, before they joined. A class with unwritten notes
          is the only row that asks for anything, so it is the only one that
          carries an accent. */}
      {!data.listing_only && classes.length > 0 && (
        <Card className="mb-4" padding={0}>
          <div className="flex items-center justify-between gap-3 px-[18px] pb-2 pt-4">
            <Eyebrow>Your classes</Eyebrow>
            {notesOwed > 0 && (
              <span className="font-sans text-[13px] text-[var(--accent-yellow-text)]" data-testid="program-notes-owed">
                {notesOwed} {notesOwed === 1 ? "session needs" : "sessions need"} notes
              </span>
            )}
          </div>
          {shownClasses.map((c, i) => (
            <ListRow
              key={c.class_id}
              borderBottom={i < shownClasses.length - 1}
              title={c.name}
              /* What the instructor has to know before they turn up: how many
                 students, and how many of them cannot be recorded. An org's
                 imported roster arrives with no consent at all, and finding
                 that out on the record screen is finding it out too late. */
              subtitle={[
                `${c.member_count} student${c.member_count === 1 ? "" : "s"}`,
                c.without_consent > 0 ? `${c.without_consent} without consent` : null,
                c.sessions_taught > 0
                  ? `${c.sessions_taught} session${c.sessions_taught === 1 ? "" : "s"} taught`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              meta={c.next_session_at ? `Next ${dateOnly(c.next_session_at)}` : "No upcoming session"}
              trailing={
                c.archived ? (
                  <Tag>Archived</Tag>
                ) : c.notes_missing > 0 ? (
                  <Tag accent>{c.notes_missing} to write</Tag>
                ) : classesComplete ? (
                  <Icon name="check" size={17} className="text-[var(--state-success)]" />
                ) : null
              }
              /* The sessions page reads the scheduling mirror and is only
                 mounted once the cutover flags are on; before that the row
                 stays a summary rather than a link into a redirect. It lives
                 under this program (I8), so Back comes home. */
              onPress={
                SCHEDULING_ENABLED && SCHEDULER_VIEW_ENABLED
                  ? () => navigate(programClassPath(user, programId, c.class_id))
                  : undefined
              }
            />
          ))}
          {archivedClasses.length > 0 && (
            <div className="border-t border-[var(--studio-divider)] px-[18px] py-3">
              <button
                type="button"
                data-testid="program-archived-toggle"
                onClick={() => setShowArchived((v) => !v)}
                className="font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              >
                {showArchived ? "Hide archived" : `Show ${archivedClasses.length} archived`}
              </button>
            </div>
          )}
          {/* A backlog Hans could not read is not a backlog of zero, and a
              row of green checks would say it was. */}
          {!classesComplete && (
            <div
              data-testid="classes-incomplete"
              className="px-[18px] py-3 font-sans text-[13px] text-[var(--text-tertiary)]"
            >
              Hans couldn&rsquo;t check every session for notes, so anything missing may not be listed here.
            </div>
          )}
        </Card>
      )}

      {/* What you read once, before you joined, and what you need to turn up
          (I3): side by side at desktop width rather than four full-width
          cards stacked down a page whose only news is above. Only at xl —
          inside the rail, two columns at a tablet's width truncate. */}
      <div className="mb-4 grid items-start gap-4 xl:grid-cols-2" data-testid="program-reference">
      <Card>
        <Eyebrow>Details</Eyebrow>
        <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {[
            ["Pay", program.compensation],
            ["Instruments", <InstrumentList key="i" instruments={program.instruments} teaches={teaches} />],
            ["Location", program.location],
            ["When it runs", program.meeting_times],
            ["Dates", dateRange(program.start_date, program.end_date)],
          ]
            .filter(([, v]) => v)
            .map(([label, value]) => (
              <div key={label}>
                <dt className="font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">{label}</dt>
                <dd className="mt-0.5 font-sans text-[14px] text-[var(--text-primary)]">{value}</dd>
              </div>
            ))}
        </dl>
      </Card>

      {program.instructor_brief && (
        <Card data-testid="program-brief">
          <Eyebrow>Before you go</Eyebrow>
          {/* Lesson markdown, so the org's bold, italic and underline read
              as it wrote them (ProgramListingSettings). */}
          <RichText
            value={program.instructor_brief}
            className="mt-2 font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]"
            testId="program-brief-text"
          />
        </Card>
      )}

      {(schoolContacts.length > 0 || orgContacts.length > 0) && (
        <Card data-testid="program-contacts">
          {schoolContacts.length > 0 && (
            <div data-testid="program-school-contacts">
              <Eyebrow>School contact</Eyebrow>
              <ContactList contacts={schoolContacts} />
            </div>
          )}
          {orgContacts.length > 0 && (
            <div
              className={schoolContacts.length > 0 ? "mt-5 border-t border-[var(--studio-divider)] pt-4" : ""}
              data-testid="program-org-contacts"
            >
              <Eyebrow>{program.org_name ? `${program.org_name} contact` : "Organization contact"}</Eyebrow>
              <ContactList contacts={orgContacts} />
            </div>
          )}
        </Card>
      )}

      </div>

      {/* The organization's files for everyone teaching here: a safety plan,
          the school's floor plan. Tiles, each opening its file. */}
      {!data.listing_only && resources.length > 0 && (
        <div className="mb-4" data-testid="program-resources">
          <Eyebrow>Resources</Eyebrow>
          <div className="mt-3">
            <ResourceTiles resources={resources} testId="program-resource-tiles" />
          </div>
        </div>
      )}

      {/* Applying from the details page, so reading the full description and
          acting on it don't need a trip back to the list. */}
      {program.can_apply && (
        <Card className="mb-4">
          <Eyebrow>Interested?</Eyebrow>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            data-testid="detail-apply-message"
            placeholder="Optional message to the organization"
            className="mt-3 w-full rounded-[10px] border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-[14px] text-[var(--text-primary)]"
          />
          <Button className="mt-3" onClick={apply} disabled={applying} data-testid="detail-apply-submit">
            {applying ? "Sending…" : "Send application"}
          </Button>
        </Card>
      )}

      {program.my_application?.status === "pending" && !program.is_member && (
        <p className="mb-4 font-sans text-[14px] text-[var(--text-tertiary)]" data-testid="detail-applied">
          You applied to this program. The organization will get back to you.
        </p>
      )}

      {program.is_member && (
        <div className="mt-6">
          <Button variant="ghost" onClick={leave} disabled={leaving} data-testid="leave-program">
            {leaving ? "Leaving…" : "Leave this program"}
          </Button>
          <p className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]">
            Every lesson you've taught under this program is kept. Leaving only stops new lessons being attributed to it.
          </p>
        </div>
      )}
      </>
      )}
    </div>
  );
}

/** Each contact on its own lines: a name, then a tappable email and phone. */
function ContactList({ contacts }) {
  return (
    <div className="mt-3 flex flex-col gap-3">
      {contacts.map((c, i) => (
        <div key={c.admin_id || `${c.name}-${c.email}-${i}`} className="flex flex-col gap-0.5">
          {c.name && <span className="font-sans text-[14px] text-[var(--text-primary)]">{c.name}</span>}
          {c.email && (
            <a href={`mailto:${c.email}`} className="font-sans text-[14px] text-[var(--accent-yellow-text)] hover:underline">
              {c.email}
            </a>
          )}
          {c.phone && (
            <a href={`tel:${c.phone}`} className="font-sans text-[14px] text-[var(--accent-yellow-text)] hover:underline">
              {c.phone}
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The Opportunities filter bar: where you're searching from, how far, and
 * whether to read it as a list or a map.
 *
 * The radius applies to BOTH views, deliberately — a list and a map showing
 * different sets of the same search is the kind of inconsistency nobody ever
 * quite trusts again.
 */
function OpportunityFilters({ view, setView, radius, setRadius, origin, area, onSearchArea, onClearArea, searching, unlocatedCount }) {
  const [query, setQuery] = useState("");
  const active = area || origin;

  return (
    <div className="mb-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl
          testId="opportunities-view"
          value={view}
          onChange={setView}
          options={[
            { id: "list", label: "List", testId: "view-list" },
            { id: "map", label: "Map", testId: "view-map" },
          ]}
        />
        <select
          value={radius ?? ""}
          onChange={(e) => setRadius(e.target.value ? Number(e.target.value) : null)}
          data-testid="radius-select"
          aria-label="Distance"
          className="rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-4 py-[9px] font-sans text-[15px] text-[var(--text-primary)]"
        >
          {RADIUS_OPTIONS.map((r) => (
            <option key={r} value={r}>
              Within {r} miles
            </option>
          ))}
          <option value="">Anywhere</option>
        </select>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (query.trim()) onSearchArea(query.trim());
        }}
        className="flex flex-wrap items-center gap-2"
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search another area — town, city, or ZIP"
          data-testid="area-search"
          className="min-w-0 flex-1 rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-4 py-[9px] font-sans text-[15px] text-[var(--text-primary)]"
        />
        <Button type="submit" variant="ghost" disabled={searching || !query.trim()}>
          {searching ? "Searching…" : "Search"}
        </Button>
      </form>

      <p className="font-sans text-[13px] text-[var(--text-tertiary)]">
        {active ? (
          <>
            Showing programs near <span className="text-[var(--text-secondary)]">{active.place}</span>
            {area && (
              <>
                {" · "}
                <button type="button" onClick={onClearArea} className="underline hover:text-[var(--text-primary)]" data-testid="clear-area">
                  back to {origin ? origin.place : "everywhere"}
                </button>
              </>
            )}
          </>
        ) : (
          // No origin is a real state: an instructor who never filled in their
          // city has nothing to measure from. Say so and say how to fix it,
          // rather than showing an empty map.
          <>
            Add your city in{" "}
            <a href="/settings" className="underline hover:text-[var(--text-primary)]">
              Settings
            </a>{" "}
            to sort these by distance.
          </>
        )}
        {unlocatedCount > 0 && (
          <>
            {" · "}
            {unlocatedCount} program{unlocatedCount === 1 ? "" : "s"} {unlocatedCount === 1 ? "hasn't" : "haven't"} set a
            location, so {unlocatedCount === 1 ? "it's" : "they're"} always shown
          </>
        )}
      </p>
    </div>
  );
}

/* --------------------------------------------------------------- the page */

export default function ProgramsPage() {
  const { user, refreshUser } = useContext(AuthContext) || {};
  const { userId, programId } = useParams();
  const navigate = useNavigate();
  const showOpportunities = programOpportunitiesEnabled(user);
  const showInvites = programInvitesEnabled(user);
  // ?tab=invites is where the invitation email's button lands. It falls back to
  // "mine" when the tab is switched off — the link keeps resolving to a page
  // that works rather than selecting a tab nothing renders.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const wanted = searchParams.get("tab");
    return wanted === "invites" || wanted === "updates" ? wanted : "mine";
  });
  // A flag can flip mid-session (the ops override propagates within the cache
  // TTL, and refreshUser re-reads it), so a tab that disappears under someone
  // has to hand them back to the one that is always there.
  const tabIsGone =
    (tab === "opportunities" && !showOpportunities) || (tab === "invites" && !showInvites);
  const activeTab = tabIsGone ? "mine" : tab;
  const [mine, setMine] = useState([]);
  /* My programs is three lists (I4): the ones you teach now, the ones you
     did, and the organizations you belong to. The choice is seeded from the
     URL; with nothing chosen it lands on the first list that has anything. */
  const [subChoice, setSubChoice] = useState(() => searchParams.get("sub") || null);
  const [listings, setListings] = useState([]);
  const [invites, setInvites] = useState([]);
  const [decidingInvite, setDecidingInvite] = useState(null);
  // Seeded from the auth payload so the tab carries its digit before the tab
  // has ever been opened, then kept live by the banner that renders inside it.
  const [orgInviteCount, setOrgInviteCount] = useState(user?.pending_org_invites || 0);
  // The Updates tab's digit: the nav badge's number until the Inbox has
  // loaded and reports its own, then whatever it reports as rows are read.
  const [updatesUnread, setUpdatesUnread] = useState(user?.unseen_program_updates || 0);
  const [loading, setLoading] = useState(true);
  const [applyingTo, setApplyingTo] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  // Opportunities view state. `origin` is where the instructor is (server-
  // resolved from their city); `area` overrides it when they search somewhere
  // else, so "back to <my town>" is always one click away.
  const [view, setView] = useState("list");
  const [radius, setRadius] = useState(RADIUS_DEFAULT);
  const [origin, setOrigin] = useState(null);
  const [area, setArea] = useState(null);
  const [searching, setSearching] = useState(false);

  // The API only ever answers for the caller, so a URL naming a different
  // instructor would render this instructor's own data under someone else's
  // address. Normalize to the canonical path instead (this also resolves the
  // "me" placeholder the sidebar uses before auth lands).
  useEffect(() => {
    if (user?.user_id && userId !== user.user_id) {
      navigate(programId ? programDetailPath(user, programId) : programsPath(user), { replace: true });
    }
  }, [user, userId, programId, navigate]);

  const load = useCallback(async () => {
    // One fetch per tab, and each one independent: a switched-off tab 404s, and
    // a single 404 must not blank a page whose other tabs are fine — which is
    // what a bare Promise.all inside one try/catch would do.
    //
    // A 404 on a switchable tab is swallowed rather than toasted, because it is
    // almost always the switch: flipped mid-session, or this render landing
    // before /auth/me has told us which tabs exist. Anything else still speaks.
    const optional = (path, enabled) =>
      enabled
        ? api.get(path).catch((err) => {
            if (err?.response?.status !== 404) toast.error(getErrorMessage(err, "Couldn't load your programs"));
            return null;
          })
        : Promise.resolve(null);

    const [memberships, opportunities, invitations] = await Promise.all([
      api.get("/marketplace/memberships").catch((err) => {
        toast.error(getErrorMessage(err, "Couldn't load your programs"));
        return null;
      }),
      optional("/marketplace/programs", showOpportunities),
      optional("/marketplace/invites", showInvites),
    ]);
    setMine(memberships?.data.programs || []);
    setListings(opportunities?.data.programs || []);
    setOrigin(opportunities?.data.origin || null);
    setInvites(invitations?.data.invites || []);
    setLoading(false);
  }, [showOpportunities, showInvites]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = async (id) => {
    setBusy(true);
    try {
      await api.post(`/marketplace/programs/${id}/apply`, { message: message.trim() || undefined });
      toast.success("Application sent");
      setApplyingTo(null);
      setMessage("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't send your application"));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (applicationId) => {
    if (!window.confirm("Withdraw your application?")) return;
    try {
      await api.delete(`/marketplace/applications/${applicationId}`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't withdraw the application"));
    }
  };

  const decideInvite = async (inviteId, action) => {
    setDecidingInvite(inviteId);
    try {
      await api.post(`/marketplace/invites/${inviteId}/${action}`);
      toast.success(action === "accept" ? "You're in — welcome aboard" : "Invitation declined");
      // Refresh the AUTH payload too: the nav dot is read off it, and leaving
      // it stale means the dot survives answering the last invitation.
      await Promise.all([load(), refreshUser?.()]);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't answer that invitation"));
    } finally {
      setDecidingInvite(null);
    }
  };

  const searchArea = async (q) => {
    setSearching(true);
    try {
      const res = await api.get("/marketplace/geocode", { params: { q } });
      if (!res.data.result) {
        // A place we can't resolve is a normal answer to a search box, so it
        // gets a plain message rather than an error toast.
        toast("We couldn't find that place — try a town, city, or ZIP.");
        return;
      }
      setArea(res.data.result);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't search that area"));
    } finally {
      setSearching(false);
    }
  };

  if (programId) {
    return (
      <PageShell width="wide">
        <ProgramDetail
          programId={programId}
          teaches={user?.instruments_taught}
          onBack={() => navigate(programsPath(user))}
          onApplied={() => {
            load();
            navigate(programsPath(user));
          }}
          onLeft={() => {
            load();
            navigate(programsPath(user));
          }}
        />
      </PageShell>
    );
  }

  const active = mine.filter((p) => p.active);
  const past = mine.filter((p) => !p.active);
  const orgs = user?.org_memberships || [];
  const sub = subChoice || (active.length ? "active" : orgs.length ? "organizations" : "active");
  const subTabs = [
    { id: "active", label: "Active", count: active.length || undefined, testId: "sub-active" },
    { id: "previous", label: "Previous", count: past.length || undefined, testId: "sub-previous" },
    // Only when there is one: the Organizations list is the Spec 11
    // consent-withdrawal path and must not disappear, but a solo instructor
    // has nothing to withdraw from.
    ...(orgs.length ? [{ id: "organizations", label: "Organizations", count: orgs.length, testId: "sub-organizations" }] : []),
  ];

  // An explicit area search wins over the instructor's own location, so the
  // whole tab — list AND map — answers the same question at once.
  // What this instructor teaches, for the instrument highlight. From the
  // profile they already filled in at onboarding — nothing new to ask.
  const teaches = user?.instruments_taught;
  const pendingInvites = invites.filter((i) => i.status === "pending");
  const answeredInvites = invites.filter((i) => i.status !== "pending");
  const tabs = [
    { id: "mine", label: "My programs", count: active.length, testId: "tab-my-programs" },
    // The Inbox (I6, decision D9): every update across every program, so the
    // digit the nav badge carries is answered here.
    { id: "updates", label: "Updates", count: updatesUnread || undefined, testId: "tab-updates" },
    ...(showOpportunities ? [{ id: "opportunities", label: "Opportunities", testId: "tab-opportunities" }] : []),
    // Counted, not just present: the tab is how someone finds out an
    // invitation is waiting, so the number belongs where they look.
    ...(showInvites
      ? [
          {
            id: "invites",
            label: "Invites",
            // Organization invitations answer here too, so they are part of
            // the number — a tab that says "1" while holding two is worse
            // than one that says nothing.
            count: pendingInvites.length + orgInviteCount || undefined,
            testId: "tab-invites",
          },
        ]
      : []),
  ];
  const searchOrigin = area || origin;
  const measured = withDistance(listings, searchOrigin);
  const visibleListings = withinRadius(measured, searchOrigin ? radius : null);
  const mappable = visibleListings.filter(hasCoords);

  return (
    <PageShell width="wide">
      <Eyebrow>Programs</Eyebrow>
      <h1 className="mb-4 mt-1 font-serif text-2xl font-semibold text-[var(--text-primary)]">
        {activeTab === "mine"
          ? "Your programs"
          : activeTab === "updates"
            ? "Updates"
            : activeTab === "invites"
              ? "Invitations"
              : "Teaching opportunities"}
      </h1>

      {/* An ORGANIZATION invitation is a different object from a program one —
          it says "join this organization", and a program is something the org
          adds you to afterwards — but it is still an invitation, and the place
          an instructor looks for one is the Invites tab. So that is where it
          answers; only its heading keeps the two apart.

          Above the tabs is the FALLBACK, for when the Invites tab is switched
          off. That flag governs PROGRAM invitations, and an org invitation must
          not disappear with a surface it has nothing to do with. */}
      {!showInvites && <OrgInviteBanner onCount={setOrgInviteCount} onChanged={refreshUser} />}

      {/* Rendered only when there is a choice to make: with both switchable
          tabs off, a one-option control is chrome that decides nothing. */}
      {tabs.length > 1 && (
        /* Three tabs with their counts are wider than a phone; the bar
           scrolls within itself rather than pushing the page sideways. */
        <div className="-mx-4 mb-6 overflow-x-auto px-4 md:mx-0 md:px-0">
          <SegmentedControl
            testId="programs-tabs"
            className="whitespace-nowrap"
            value={activeTab}
            onChange={setTab}
            options={tabs}
          />
        </div>
      )}

      {loading ? (
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>
      ) : activeTab === "updates" ? (
        <UpdatesInbox onUnread={setUpdatesUnread} />
      ) : activeTab === "invites" ? (
        <div className="flex flex-col gap-4">
          {/* First, because joining the organization is the decision the
              program invitations underneath may depend on. Renders nothing
              when there is none. */}
          <OrgInviteBanner onCount={setOrgInviteCount} onChanged={refreshUser} />

          {invites.length === 0 && orgInviteCount === 0 ? (
          <Card>
            <p className="font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="invites-empty">
              No invitations right now. Organizations can find you and invite you to programs when your profile is open
              to work —{" "}
              <a href="/settings" className="font-semibold text-[var(--accent-yellow-text)] hover:underline">
                turn that on in Settings
              </a>
              .
            </p>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            {pendingInvites.map((inv) => (
              <InviteCard
                key={inv.invite_id}
                invite={inv}
                teaches={teaches}
                busy={decidingInvite === inv.invite_id}
                onAccept={() => decideInvite(inv.invite_id, "accept")}
                onDecline={() => decideInvite(inv.invite_id, "decline")}
              />
            ))}
            {answeredInvites.length > 0 && (
              <>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">
                  Already answered
                </p>
                {answeredInvites.map((inv) => (
                  <InviteCard key={inv.invite_id} invite={inv} teaches={teaches} />
                ))}
              </>
            )}
          </div>
        )}
        </div>
      ) : activeTab === "mine" ? (
        <>
        {/* Rendered only when there is a choice to make. Its own line under
            the page's tabs, scrolling within itself on a phone. */}
        {(mine.length > 0 || orgs.length > 0) && (
          <div className="-mx-4 mb-5 overflow-x-auto px-4 md:mx-0 md:px-0">
            {/* Text tabs, not a second pill bar: this filters the list under
                the page's tabs and must not read as a copy of them. */}
            <TextTabs
              testId="my-programs-sub"
              className="whitespace-nowrap"
              value={sub}
              onChange={setSubChoice}
              options={subTabs}
            />
          </div>
        )}
        {sub === "organizations" && orgs.length > 0 ? (
          /* The organizations this instructor belongs to, with the per-org
             Leave — the consent-withdrawal path from Spec 11. Its own list
             (decision D8): leaving a program and leaving an organization are
             different acts, and a program action never sits beside this one. */
          <div data-testid="organizations-block">
            <OrgMembershipCard />
          </div>
        ) : sub === "previous" ? (
          past.length === 0 ? (
            <Card>
              <p className="font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="previous-empty">
                Nothing here yet. Programs that have ended, or that you have left, are kept here.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="previous-programs">
              {past.map((p) => (
                <MembershipTile
                  key={p.program_id}
                  program={p}
                  onOpen={() => navigate(programDetailPath(user, p.program_id))}
                />
              ))}
            </div>
          )
        ) : active.length === 0 ? (
          <Card>
            <p className="font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="my-programs-empty">
              You're not teaching in any programs{past.length ? " right now" : " yet"}.
              {/* Only offered while the board is up — a dead end reads worse
                  than a plain statement of where things stand. */}
              {showOpportunities && (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => setTab("opportunities")}
                    className="font-semibold text-[var(--accent-yellow-text)] hover:underline"
                  >
                    Browse opportunities
                  </button>
                </>
              )}
              .
            </p>
          </Card>
        ) : (
          /* Two abreast on the wide shell (I3): a tile is a title, an org and
             a line of facts, which is half a row, not a whole one. */
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="active-programs">
            {active.map((p) => (
              <MembershipTile
                key={p.program_id}
                program={p}
                onOpen={() => navigate(programDetailPath(user, p.program_id))}
              />
            ))}
          </div>
        )}
        </>
      ) : listings.length === 0 ? (
        <Card>
          <p className="font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="marketplace-empty">
            No open programs right now — check back soon.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="font-sans text-[14px] text-[var(--text-tertiary)]">
            Programs from music organizations looking for instructors. Apply and the organization will get back to you.
          </p>

          <OpportunityFilters
            view={view}
            setView={setView}
            radius={radius}
            setRadius={setRadius}
            origin={origin}
            area={area}
            searching={searching}
            onSearchArea={searchArea}
            onClearArea={() => setArea(null)}
            unlocatedCount={visibleListings.filter((p) => !hasCoords(p)).length}
          />

          {/* The map ALWAYS renders in map view, even with nothing to plot.
              Replacing it with a text card meant the one view whose job is to
              show you where things are disappeared exactly when you most
              wanted to see the area you were searching — and with an origin
              set, an empty map still says something useful: here is your
              radius, and nothing is in it. */}
          {view === "map" && (
            <>
              <ProgramsMap
                programs={mappable}
                origin={searchOrigin}
                radius={radius}
                teaches={user?.instruments_taught}
                onSelect={(p) => navigate(programDetailPath(user, p.program_id))}
              />
              {mappable.length === 0 && (
                <p className="font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="map-empty">
                  {visibleListings.length === 0
                    ? `Nothing within ${radius} miles of ${searchOrigin?.place || "here"} — widen the distance or search another area.`
                    : "None of these programs has set a location yet, so there's nothing to pin. They're all in the list below."}
                </p>
              )}
            </>
          )}

          {visibleListings.length === 0 && view === "list" && (
            <Card>
              <p className="font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="radius-empty">
                No programs within {radius} miles of {searchOrigin?.place || "here"}. Try a wider distance or search
                another area.
              </p>
            </Card>
          )}

          {visibleListings.map((p) => {
            const status = p.my_application ? APP_STATUS[p.my_application.status] : null;
            return (
              <Card key={p.program_id} data-testid={`marketplace-program-${p.program_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-sans text-[17px] font-semibold text-[var(--text-primary)]">{p.title}</span>
                  {p.is_member && <Tag accent>You teach here</Tag>}
                  {status && !p.is_member && <Tag accent={status.accent}>{status.label}</Tag>}
                  {p.status !== "open" && <Tag>{p.status === "filled" ? "Filled" : "Closed"}</Tag>}
                  {matchingInstruments(p.instruments, teaches).length > 0 && (
                    <Tag accent data-testid={`instrument-match-tag-${p.program_id}`}>
                      Matches your instruments
                    </Tag>
                  )}
                </div>
                <p className="mt-0.5 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">{p.org_name}</p>

                {/* Essentials only. The description is the single longest thing
                    on a listing and the least useful when scanning a dozen of
                    them — it moved to the details page, and what's left is what
                    an instructor decides on: pay, instruments, when, where. */}
                <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
                  {[
                    ["Pay", p.compensation],
                    ["Instruments", <InstrumentList key="i" instruments={p.instruments} teaches={teaches} />],
                    ["When", metaLine([p.meeting_times, dateRange(p.start_date, p.end_date)])],
                    ["Where", metaLine([p.location || "Location not set", distanceLabel(p.distance_miles)])],
                  ]
                    .filter(([, v]) => v)
                    .map(([label, value]) => (
                      <div key={label}>
                        <dt className="font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">
                          {label}
                        </dt>
                        <dd className="mt-0.5 font-sans text-[14px] text-[var(--text-primary)]">{value}</dd>
                      </div>
                    ))}
                </dl>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    data-testid={`marketplace-details-${p.program_id}`}
                    onClick={() => navigate(programDetailPath(user, p.program_id))}
                  >
                    Details
                  </Button>
                  {p.can_apply && applyingTo !== p.program_id && (
                    <Button
                      data-testid={`marketplace-apply-${p.program_id}`}
                      onClick={() => setApplyingTo(p.program_id)}
                    >
                      Apply
                    </Button>
                  )}
                </div>
                {applyingTo === p.program_id && (
                  <div className="mt-3 flex flex-col gap-2">
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={3}
                      data-testid="marketplace-apply-message"
                      placeholder="Optional message to the organization"
                      className="w-full rounded-[10px] border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-[14px] text-[var(--text-primary)]"
                    />
                    <div className="flex gap-2">
                      <Button data-testid="marketplace-apply-submit" onClick={() => apply(p.program_id)} disabled={busy}>
                        {busy ? "Sending…" : "Send application"}
                      </Button>
                      <Button variant="ghost" onClick={() => setApplyingTo(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                {p.my_application?.status === "pending" && (
                  <Button
                    className="mt-3"
                    variant="ghost"
                    data-testid={`marketplace-withdraw-${p.program_id}`}
                    onClick={() => withdraw(p.my_application.application_id)}
                  >
                    Withdraw
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
